import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Box, Typography, CircularProgress, Grid, Card, CardContent, Avatar,
    Chip, Button, IconButton, Checkbox, Tooltip, Select, MenuItem,
    FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, Paper, Divider, Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

const getAvatarColor = (name) => {
    const colors = [
        '#0ea5e9', '#10b981', '#ec4899', '#8b5cf6', '#f59e0b', '#f43f5e',
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash % colors.length)];
};

export default function SubordinatesTab({ selectedUser, orgId, onOpenSubordinate, onDelete }) {
    const { axios, token } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // ── Selection State ───────────────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [newSupervisorId, setNewSupervisorId] = useState('');
    const [reassigning, setReassigning] = useState(false);

    const userId = selectedUser?.rawData?.user?.userId || selectedUser?.user?.userId;
    const userType = selectedUser?.facultyInfo?.facultyType || selectedUser?.facultyType || 'FACULTY';

    // Labels based on the current user's role
    let subordinatesLabel = 'Team Members';
    let subordinateRoleLabel = 'TEAM MEMBER';
    let expectedSupervisorType = '';  // The role that can supervise subordinates at this level

    if (userType === 'SAMANVAYKARTA') {
        subordinatesLabel = 'Paryavekshaks';
        subordinateRoleLabel = 'PARYAVEKSHAK';
        expectedSupervisorType = 'SAMANVAYKARTA';
    } else if (userType === 'PARYAVEKSHAK') {
        subordinatesLabel = 'Nirvahaks';
        subordinateRoleLabel = 'NIRVAHAK';
        expectedSupervisorType = 'PARYAVEKSHAK';
    }

    const selectedUserObj = selectedUser?.rawData?.user || selectedUser?.user || selectedUser;
    const supervisorName = [selectedUserObj?.firstName, selectedUserObj?.middleName, selectedUserObj?.lastName]
        .filter(Boolean).join(' ').trim() || selectedUserObj?.email || 'Supervisor';

    const showAddButton = userType === 'SAMANVAYKARTA' || userType === 'PARYAVEKSHAK';
    const addButtonLabel = userType === 'SAMANVAYKARTA' ? 'Add Paryavekshak' : 'Add Nirvahak';

    // Fetch all org faculty
    const { data: allFaculty, isLoading: loadingFaculty } = useQuery({
        queryKey: ['allFacultyHierarchy', orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                params: { userType: 'FACULTY', page: 1, pageSize: 2000 },
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data?.items || response.data?.content || [];
        },
        enabled: !!orgId && !!userId,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // Build a lookup map of all faculty members
    const facultyMap = useMemo(() => {
        const map = new Map();
        if (allFaculty) {
            allFaculty.forEach(f => {
                const uId = f.user?.userId || f.userId;
                const email = f.user?.email || f.email;
                const empId = f.facultyInfo?.employeeId || f.employeeId;
                const phone = f.user?.phoneNumber || f.phoneNumber;
                if (uId) map.set(String(uId).toLowerCase().trim(), f);
                if (email) map.set(String(email).toLowerCase().trim(), f);
                if (empId) map.set(String(empId).toLowerCase().trim(), f);
                if (phone) map.set(String(phone).toLowerCase().trim(), f);
            });
        }
        return map;
    }, [allFaculty]);

    // Direct subordinates of the current user
    const subordinates = useMemo(() => {
        if (!allFaculty || !userId) return [];
        return allFaculty.filter(f => {
            const rawSupId = f.facultyInfo?.supervisorId || f.supervisorId;
            if (!rawSupId) return false;
            const resolvedSupervisor = facultyMap.get(String(rawSupId).toLowerCase().trim());
            const resolvedSupervisorId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : null;
            return (
                (resolvedSupervisorId && String(resolvedSupervisorId).toLowerCase().trim() === String(userId).toLowerCase().trim()) ||
                String(rawSupId).toLowerCase().trim() === String(userId).toLowerCase().trim()
            );
        });
    }, [allFaculty, userId, facultyMap]);

    // Potential reassign targets: same-tier peers (same role), excluding current user
    const reassignTargets = useMemo(() => {
        if (!allFaculty || !expectedSupervisorType) return [];
        return allFaculty.filter(f => {
            const fType = f.facultyInfo?.facultyType || f.facultyType;
            const fId = f.user?.userId || f.userId;
            const status = f.user?.userStatus || f.userStatus;
            const isInactive = status === 'INACTIVE' || status === 'inactive';
            return fType === expectedSupervisorType && fId !== userId && !isInactive;
        });
    }, [allFaculty, expectedSupervisorType, userId]);

    // Fetch all org classes to build termToClassIds mapping
    const { data: allClasses, isLoading: loadingClasses } = useQuery({
        queryKey: ['allClassesComplete', orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data || [];
        },
        enabled: !!orgId,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // Fetch student counts per classId
    const { data: classIdToStudentCount, isLoading: loadingCounts } = useQuery({
        queryKey: ['classStudentCounts', orgId, (allClasses || []).map(c => c.classId).join(',')],
        queryFn: async () => {
            if (!orgId || !allClasses || allClasses.length === 0) return new Map();
            const countMap = new Map();
            const promises = allClasses.map(cls =>
                axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
                    params: { page: 0, pageSize: 1000 },
                    headers: { Authorization: `Bearer ${token}` }
                }).then(res => {
                    let count = 0;
                    if (res.data?.totalElements !== undefined) count = res.data.totalElements;
                    else if (res.data?.totalCount !== undefined) count = res.data.totalCount;
                    else if (res.data?.total !== undefined) count = res.data.total;
                    else {
                        const students = res.data?.items || res.data?.content || res.data || [];
                        count = Array.isArray(students) ? students.length : 0;
                    }
                    if (cls.classId) countMap.set(cls.classId, count);
                    if (cls.className) countMap.set(cls.className.trim(), count);
                    if (cls.className) countMap.set(cls.className.trim().toLowerCase(), count);
                }).catch(() => {
                    if (cls.classId) countMap.set(cls.classId, 0);
                })
            );
            await Promise.all(promises);
            return countMap;
        },
        enabled: !!orgId && !!(allClasses && allClasses.length > 0),
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const termToClassIds = useMemo(() => {
        const mapping = new Map();
        const addMapping = (term, classId) => {
            if (!term || !classId) return;
            const clean = String(term).toLowerCase().trim();
            if (!mapping.has(clean)) mapping.set(clean, []);
            const arr = mapping.get(clean);
            if (!arr.includes(classId)) arr.push(classId);
        };
        if (allClasses) {
            allClasses.forEach(cls => {
                if (cls.classId) {
                    addMapping(cls.classId, cls.classId);
                    if (cls.className) addMapping(cls.className, cls.classId);
                    if (cls.sectionName) addMapping(cls.sectionName, cls.classId);
                    const fullLabel = `${cls.className} ${cls.sectionName || ''}`.trim();
                    addMapping(fullLabel, cls.classId);
                }
            });
        }
        return mapping;
    }, [allClasses]);

    const getNirvahakStudentCount = useCallback((nirvahakFaculty) => {
        if (!classIdToStudentCount) return 0;
        const department =
            nirvahakFaculty.user?.additionalAttributes?.assignedClasses ||
            nirvahakFaculty.additionalAttributes?.assignedClasses || '';
        if (!department) return 0;
        const depts = department.split(',').map(d => d.trim());
        const matchedClassIds = new Set();
        depts.forEach(dept => {
            if (dept) {
                matchedClassIds.add(dept);
                matchedClassIds.add(dept.toLowerCase());
            }
            const ids = termToClassIds.get(dept.toLowerCase()) || [];
            ids.forEach(id => matchedClassIds.add(id));
        });
        let count = 0;
        matchedClassIds.forEach(classId => {
            count += (classIdToStudentCount.get(classId) || 0);
        });
        return count;
    }, [classIdToStudentCount, termToClassIds]);

    const totalStudents = useMemo(() => {
        if (!subordinates.length || !allFaculty) return 0;
        if (userType === 'PARYAVEKSHAK') {
            return subordinates.reduce((sum, nir) => sum + getNirvahakStudentCount(nir), 0);
        }
        if (userType === 'SAMANVAYKARTA') {
            const subordinateIds = new Set(subordinates.map(f => String(f.user?.userId || f.userId || '').toLowerCase().trim()));
            const nirvahaks = allFaculty.filter(f => {
                const type = f.facultyInfo?.facultyType || f.facultyType;
                const rawSupId = f.facultyInfo?.supervisorId || f.supervisorId;
                const resolvedSupervisor = rawSupId ? facultyMap.get(String(rawSupId).toLowerCase().trim()) : null;
                const supId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : rawSupId;
                const cleanSupId = supId ? String(supId).toLowerCase().trim() : '';
                return type === 'NIRVAHAK' && cleanSupId && subordinateIds.has(cleanSupId);
            });
            return nirvahaks.reduce((sum, nir) => sum + getNirvahakStudentCount(nir), 0);
        }
        return 0;
    }, [subordinates, allFaculty, userType, getNirvahakStudentCount, facultyMap]);

    const getInitials = (name) => {
        return (name || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    // ── Selection Handlers ────────────────────────────────────────────────────
    const toggleSelect = (subId, e) => {
        e.stopPropagation();
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(subId)) next.delete(subId);
            else next.add(subId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const activeSubIds = subordinates
            .filter(f => {
                const status = f.user?.userStatus || f.userStatus;
                return status !== 'INACTIVE' && status !== 'inactive';
            })
            .map(f => f.user?.userId || f.userId);

        const allSelected = activeSubIds.every(id => selectedIds.has(id));
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(activeSubIds));
        }
    };

    const activeSubordinates = subordinates.filter(f => {
        const status = f.user?.userStatus || f.userStatus;
        return status !== 'INACTIVE' && status !== 'inactive';
    });
    const allSelected = activeSubordinates.length > 0 && activeSubordinates.every(f => selectedIds.has(f.user?.userId || f.userId));
    const someSelected = selectedIds.size > 0;

    // ── Bulk Reassign ─────────────────────────────────────────────────────────
    const handleOpenReassign = () => {
        setNewSupervisorId('');
        setReassignDialogOpen(true);
    };

    const handleBulkReassign = async () => {
        if (!newSupervisorId) {
            toast.warn('Please select a new supervisor.');
            return;
        }
        setReassigning(true);
        try {
            const selectedSubordinates = subordinates.filter(f => selectedIds.has(f.user?.userId || f.userId));
            const promises = selectedSubordinates.map(async (sub) => {
                const subId = sub.user?.userId || sub.userId;
                const detailRes = await axios.get(`${configData.GET_USER_DETAILS}/${subId}`);
                const detail = detailRes.data;
                const payload = {
                    user: detail.user,
                    studentInfo: detail.studentInfo || null,
                    parentInfo: detail.parentInfo || null,
                    facultyInfo: {
                        ...detail.facultyInfo,
                        supervisorId: newSupervisorId
                    },
                    staffInfo: detail.staffInfo || null
                };
                await axios.put(`${configData.UPDATE_USER}/${subId}`, payload);
            });
            await Promise.all(promises);
            toast.success(`Reassigned ${selectedSubordinates.length} ${subordinatesLabel.toLowerCase()} successfully.`);
            setSelectedIds(new Set());
            setReassignDialogOpen(false);
            setNewSupervisorId('');
            queryClient.invalidateQueries(['allFacultyHierarchy', orgId]);
        } catch (err) {
            console.error('Bulk reassign error:', err);
            toast.error(`Reassignment failed: ${err?.response?.data?.message || err.message}`);
        } finally {
            setReassigning(false);
        }
    };

    const isLoading = loadingFaculty || loadingClasses;

    if (isLoading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!subordinates || subordinates.length === 0) {
        return (
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <Typography variant="body1" color="text.secondary">
                    No {subordinatesLabel.toLowerCase()} assigned under this person yet.
                </Typography>
                {showAddButton && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/Faculty/registration', {
                            state: {
                                orgId,
                                lockedFacultyType: subordinateRoleLabel,
                                supervisorId: userId,
                                supervisorName
                            }
                        })}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#0070D2',
                            '&:hover': { bgcolor: '#005fb2' }
                        }}
                    >
                        {addButtonLabel}
                    </Button>
                )}
            </Box>
        );
    }

    const selectedSubordinateNames = subordinates
        .filter(f => selectedIds.has(f.user?.userId || f.userId))
        .map(f => {
            const u = f.user || f;
            return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown';
        });

    return (
        <Box sx={{ p: 4 }}>
            {/* Top Metrics Cards Row */}
            <Box sx={{ display: 'flex', gap: 3, mb: 5, flexWrap: 'wrap' }}>
                <Card elevation={0} sx={{ flex: 1, minWidth: '220px', borderRadius: '16px', border: '1px solid #e2e8f0', p: '20px 24px', bgcolor: '#ffffff' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', mb: 1 }}>
                        Students
                    </Typography>
                    <Typography sx={{ fontSize: '32px', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                        {loadingCounts ? <CircularProgress size={24} sx={{ color: '#1e293b' }} /> : totalStudents}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 1 }}>
                        Total enrolled under {userType === 'SAMANVAYKARTA' ? 'this Samanvaykarta' : 'this Paryavekshak'}
                    </Typography>
                </Card>

                <Card elevation={0} sx={{ flex: 1, minWidth: '220px', borderRadius: '16px', border: '1px solid #e2e8f0', p: '20px 24px', bgcolor: '#ffffff' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', mb: 1 }}>
                        {subordinatesLabel}
                    </Typography>
                    <Typography sx={{ fontSize: '32px', fontWeight: 800, color: '#0070D2', lineHeight: 1.2 }}>
                        {subordinates.length}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 1 }}>
                        Direct {subordinatesLabel.toLowerCase()} assigned
                    </Typography>
                </Card>
            </Box>

            {/* Subordinates Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Select All Checkbox */}
                    {showAddButton && (
                        <Tooltip title={allSelected ? 'Deselect All' : 'Select All'}>
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected && !allSelected}
                                onChange={toggleSelectAll}
                                icon={<CheckBoxOutlineBlankIcon />}
                                checkedIcon={<CheckBoxIcon />}
                                sx={{ color: '#0070D2', p: 0.5 }}
                            />
                        </Tooltip>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {subordinatesLabel} ({subordinates.length})
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {showAddButton && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/Faculty/registration', {
                                state: {
                                    orgId,
                                    lockedFacultyType: subordinateRoleLabel,
                                    supervisorId: userId,
                                    supervisorName
                                }
                            })}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                bgcolor: '#0070D2',
                                '&:hover': { bgcolor: '#005fb2' }
                            }}
                        >
                            {addButtonLabel}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* ── Bulk Action Bar (visible when selection > 0) ── */}
            {someSelected && (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 2.5,
                        py: 1.5,
                        mb: 3,
                        borderRadius: '12px',
                        border: '1.5px solid #0070D2',
                        bgcolor: '#eff6ff',
                        flexWrap: 'wrap'
                    }}
                >
                    <Badge
                        badgeContent={selectedIds.size}
                        color="primary"
                        sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}
                    >
                        <PersonIcon sx={{ color: '#0070D2' }} />
                    </Badge>
                    <Typography sx={{ fontWeight: 600, color: '#1e40af', flex: 1 }}>
                        {selectedIds.size} {selectedIds.size === 1 ? subordinateRoleLabel : subordinatesLabel} selected
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<SwapHorizIcon />}
                        onClick={handleOpenReassign}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#0070D2',
                            '&:hover': { bgcolor: '#005fb2' }
                        }}
                    >
                        Reassign Supervisor
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setSelectedIds(new Set())}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#64748b',
                            borderColor: '#cbd5e1',
                            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                        }}
                    >
                        Clear
                    </Button>
                </Paper>
            )}

            {/* Subordinates Grid */}
            <Grid container spacing={3}>
                {subordinates.map(f => {
                    const userObj = f.user || f;
                    const subId = userObj.userId || f.userId;
                    const status = userObj.userStatus || f.userStatus;
                    const isInactive = status === 'INACTIVE' || status === 'inactive';
                    const name = `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || userObj.email;
                    const region = f.facultyInfo?.department || '';
                    const avatarColor = getAvatarColor(name);
                    const isChecked = selectedIds.has(subId);

                    // Per-subordinate student count
                    let subStudentCount = 0;
                    if (userType === 'PARYAVEKSHAK') {
                        subStudentCount = getNirvahakStudentCount(f);
                    } else if (userType === 'SAMANVAYKARTA' && allFaculty) {
                        const parId = userObj.userId || f.userId;
                        const cleanParId = parId ? String(parId).toLowerCase().trim() : '';
                        const parNirvahaks = allFaculty.filter(nf => {
                            const type = nf.facultyInfo?.facultyType || nf.facultyType;
                            const rawSupId = nf.facultyInfo?.supervisorId || nf.supervisorId;
                            const resolvedSupervisor = rawSupId ? facultyMap.get(String(rawSupId).toLowerCase().trim()) : null;
                            const supId = resolvedSupervisor ? (resolvedSupervisor.user?.userId || resolvedSupervisor.userId) : rawSupId;
                            const cleanSupId = supId ? String(supId).toLowerCase().trim() : '';
                            return type === 'NIRVAHAK' && cleanSupId === cleanParId;
                        });
                        subStudentCount = parNirvahaks.reduce((sum, nir) => sum + getNirvahakStudentCount(nir), 0);
                    }

                    return (
                        <Grid item xs={12} sm={6} md={4} key={subId}>
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: '16px',
                                    border: isChecked ? '2px solid #0070D2' : '1px solid #e2e8f0',
                                    height: '100%',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.15s ease',
                                    cursor: !isInactive ? 'pointer' : 'default',
                                    opacity: isInactive ? 0.6 : 1,
                                    filter: isInactive ? 'grayscale(100%)' : 'none',
                                    bgcolor: isChecked ? '#f0f7ff' : '#fff',
                                    '&:hover': isInactive ? {} : {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.04)',
                                    }
                                }}
                                onClick={() => !isInactive && onOpenSubordinate && onOpenSubordinate(f)}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        {/* Checkbox — stop propagation so card click doesn't fire */}
                                        {showAddButton && !isInactive && (
                                            <Checkbox
                                                checked={isChecked}
                                                onClick={(e) => toggleSelect(subId, e)}
                                                icon={<CheckBoxOutlineBlankIcon sx={{ color: '#cbd5e1' }} />}
                                                checkedIcon={<CheckBoxIcon sx={{ color: '#0070D2' }} />}
                                                sx={{ p: 0, flexShrink: 0 }}
                                            />
                                        )}
                                        <Avatar sx={{ bgcolor: avatarColor, width: 48, height: 48, fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>
                                            {getInitials(name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {name}
                                            </Typography>
                                            {region && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {region}
                                                </Typography>
                                            )}
                                            {userObj.phoneNumber && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mt: 0.5, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    📞 {userObj.phoneNumber}
                                                </Typography>
                                            )}
                                        </Box>
                                        {onDelete && !isInactive && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDelete(f);
                                                }}
                                                sx={{
                                                    color: '#ef4444',
                                                    padding: '8px',
                                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <Chip
                                            label={loadingCounts ? 'Loading...' : `${subStudentCount} Students`}
                                            size="small"
                                            sx={{
                                                bgcolor: subStudentCount > 0 ? '#eff6ff' : '#f8fafc',
                                                color: subStudentCount > 0 ? '#1d4ed8' : '#64748b',
                                                fontWeight: 700,
                                                fontSize: '11px',
                                                border: subStudentCount > 0 ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                                borderRadius: '20px'
                                            }}
                                        />
                                        <Chip
                                            label={f.facultyInfo?.facultyType || f.facultyType || subordinateRoleLabel}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(139, 92, 246, 0.1)',
                                                color: '#8b5cf6',
                                                fontWeight: 700,
                                                fontSize: '10px',
                                                borderRadius: '20px'
                                            }}
                                        />
                                        {isInactive && (
                                            <Chip
                                                label="Deactivated"
                                                size="small"
                                                sx={{
                                                    bgcolor: '#fef2f2',
                                                    color: '#ef4444',
                                                    fontWeight: 700,
                                                    fontSize: '10px',
                                                    borderRadius: '20px',
                                                    border: '1px solid #fecaca'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* ── Bulk Reassign Dialog ── */}
            <Dialog
                open={reassignDialogOpen}
                onClose={() => !reassigning && setReassignDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#1e293b', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SwapHorizIcon sx={{ color: '#0070D2' }} />
                        Reassign {subordinatesLabel}
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2.5 }}>
                    <Typography sx={{ fontSize: '14px', color: '#475569', mb: 2 }}>
                        Reassigning <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? subordinateRoleLabel : subordinatesLabel} to a new supervisor:
                    </Typography>

                    {/* Selected names list */}
                    <Paper elevation={0} sx={{ bgcolor: '#f8fafc', borderRadius: '10px', p: 2, mb: 3, maxHeight: '130px', overflowY: 'auto', border: '1px solid #e2e8f0' }}>
                        {selectedSubordinateNames.map((n, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0070D2', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>{n}</Typography>
                            </Box>
                        ))}
                    </Paper>

                    <FormControl fullWidth>
                        <InputLabel>New Supervisor ({expectedSupervisorType})</InputLabel>
                        <Select
                            value={newSupervisorId}
                            label={`New Supervisor (${expectedSupervisorType})`}
                            onChange={e => setNewSupervisorId(e.target.value)}
                            sx={{ borderRadius: '10px' }}
                        >
                            {reassignTargets.length === 0 && (
                                <MenuItem disabled value="">
                                    No other {expectedSupervisorType} supervisors available
                                </MenuItem>
                            )}
                            {reassignTargets.map(f => {
                                const u = f.user || f;
                                const fId = u.userId || f.userId;
                                const fName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || fId;
                                return (
                                    <MenuItem key={fId} value={fId}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ width: 28, height: 28, bgcolor: getAvatarColor(fName), fontSize: '11px', fontWeight: 700 }}>
                                                {getInitials(fName)}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.2 }}>{fName}</Typography>
                                                {u.email && <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{u.email}</Typography>}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setReassignDialogOpen(false)}
                        disabled={reassigning}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkReassign}
                        disabled={reassigning || !newSupervisorId}
                        startIcon={reassigning ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#0070D2',
                            '&:hover': { bgcolor: '#005fb2' }
                        }}
                    >
                        {reassigning ? 'Reassigning...' : `Reassign ${selectedIds.size} ${selectedIds.size === 1 ? subordinateRoleLabel : subordinatesLabel}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
